import { supabase, supabaseConfigured } from '@/lib/supabaseClient';
import { getActiveTenantId } from '@/lib/tenantContext';

export const CLOUD_TABLE = 'drilllog_records';
export const SYNC_QUEUE_STORE = 'SyncQueue';

// Rig operational data is published as a shift snapshot rather than live.
// Administrative/configuration records may still synchronise immediately.
export const SHIFT_SYNC_ENTITIES = new Set([
  'Sheet', 'DrillRun', 'DailyLog', 'DepthAlert', 'Handover', 'Stocktake',
  'AirCoreRun', 'AirCoreSample', 'RCRun', 'ShiftSnapshot',
]);

export function isCloudEnabled() {
  return supabaseConfigured && Boolean(supabase);
}

export async function getCloudUser() {
  if (!isCloudEnabled()) return null;
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  return data?.session?.user || null;
}

const VALID_ROLES = new Set(['rig', 'supervisor', 'admin', 'owner']);

function normalizeRole(role) {
  const value = String(role || '').trim().toLowerCase();
  return VALID_ROLES.has(value) ? value : null;
}

export async function getCloudProfile(userId) {
  if (!isCloudEnabled() || !userId) return null;
  const { data, error } = await supabase
    .from(CLOUD_TABLE)
    .select('payload,updated_at,deleted')
    .eq('user_id', userId)
    .eq('entity', 'User')
    .eq('record_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data || data.deleted || !data.payload) return null;
  return { ...data.payload, updated_date: data.payload.updated_date || data.updated_at };
}

export async function ensureCloudProfile(user, localUser = {}) {
  if (!isCloudEnabled() || !user) return null;

  // The cloud User record is authoritative for application role. In
  // particular, never let a stale offline/local 'rig' role overwrite an
  // Owner/Admin role that was assigned from another device or in Supabase.
  let cloudProfile = null;
  try {
    cloudProfile = await getCloudProfile(user.id);
  } catch (error) {
    // If the profile cannot be read, retain the existing local/metadata role
    // rather than manufacturing an upgrade or downgrade.
    console.warn('[DrillLogPro] cloud profile read skipped:', error?.message || error);
  }

  const role =
    normalizeRole(cloudProfile?.role) ||
    normalizeRole(user.user_metadata?.role) ||
    normalizeRole(localUser.role) ||
    'rig';

  const profile = {
    ...cloudProfile,
    id: user.id,
    email: user.email || cloudProfile?.email || localUser.email || null,
    name: cloudProfile?.name || localUser.name || user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    role,
    updated_date: new Date().toISOString(),
  };

  const { error } = await supabase.from(CLOUD_TABLE).upsert({
    user_id: user.id,
    entity: 'User',
    record_id: user.id,
    payload: profile,
    updated_at: profile.updated_date,
    deleted: false,
  }, { onConflict: 'user_id,entity,record_id' });
  if (error) throw error;
  return profile;
}

function queueId(userId, entity, recordId) {
  return `${userId}:${entity}:${recordId}`;
}

export async function enqueueRecord(userId, entity, record, deleted = false) {
  if (!userId || !record?.id) return;
  const organisationId = await getActiveTenantId();
  const item = {
    id: queueId(userId, entity, record.id),
    organisation_id: organisationId,
    user_id: userId,
    entity,
    record_id: record.id,
    payload: deleted ? {} : record,
    updated_at: record.updated_date || new Date().toISOString(),
    deleted: Boolean(deleted),
    queued_at: new Date().toISOString(),
  };
  await (await import('@/storage/sqliteStore')).store(SYNC_QUEUE_STORE).put(item);
}

async function removeQueueItem(itemId) {
  const { store } = await import('@/storage/sqliteStore');
  await store(SYNC_QUEUE_STORE).delete(itemId);
}

export async function pushRecord(userId, entity, record, deleted = false) {
  if (!userId || !record?.id) return;
  if (SHIFT_SYNC_ENTITIES.has(entity)) {
    // Persist locally and hold rig operational changes until End Shift or
    // Force Sync so supervisors never see a live-refreshing drilling state.
    await enqueueRecord(userId, entity, record, deleted);
    return;
  }

  if (!isCloudEnabled()) {
    await enqueueRecord(userId, entity, record, deleted);
    return;
  }

  try {
    const updatedAt = record.updated_date || new Date().toISOString();
    const organisationId = await getActiveTenantId();
    if (!organisationId) throw new Error('No active DrillLogPro organisation');
    const { error } = await supabase.from(CLOUD_TABLE).upsert({
      user_id: userId,
      organisation_id: organisationId,
      entity,
      record_id: record.id,
      payload: deleted ? {} : record,
      updated_at: updatedAt,
      deleted,
    }, { onConflict: 'user_id,entity,record_id' });
    if (error) throw error;
  } catch (error) {
    // Configuration changes must remain retryable if the network is down.
    await enqueueRecord(userId, entity, record, deleted);
    console.warn('[DrillLogPro] immediate cloud sync queued for retry:', error?.message || error);
  }
}

export async function flushSyncQueue(userId) {
  if (!userId || !isCloudEnabled()) return { pushed: 0, pending: 0, failed: 0 };
  const { store } = await import('@/storage/sqliteStore');
  const queue = await store(SYNC_QUEUE_STORE).all();
  const activeOrg = await getActiveTenantId();
  const pending = queue
    .filter((item) => item.user_id === userId && item.organisation_id === activeOrg)
    .sort((a, b) => new Date(a.queued_at || 0) - new Date(b.queued_at || 0));

  let pushed = 0;
  let failed = 0;

  const stale = queue.filter((item) => item.user_id === userId && activeOrg && item.organisation_id && item.organisation_id !== activeOrg);
  for (const item of stale) await store(SYNC_QUEUE_STORE).delete(item.id);

  for (const item of pending) {
    try {
      let payload = item.deleted ? {} : item.payload;

      // A device can legitimately update its own profile (for example a
      // company logo), but its local cached role must never overwrite the
      // cloud-authoritative role. Role changes to another user's profile are
      // still allowed through the Owner/Admin user-management flow.
      if (!item.deleted && item.entity === 'User' && item.record_id === item.user_id) {
        const cloudProfile = await getCloudProfile(item.user_id);
        if (cloudProfile?.role) {
          payload = { ...payload, role: cloudProfile.role };
        }
      }

      const { error } = await supabase.from(CLOUD_TABLE).upsert({
        user_id: item.user_id,
        organisation_id: item.organisation_id || await getActiveTenantId(),
        entity: item.entity,
        record_id: item.record_id,
        payload,
        updated_at: item.updated_at,
        deleted: item.deleted,
      }, { onConflict: 'user_id,entity,record_id' });
      if (error) throw error;
      await store(SYNC_QUEUE_STORE).delete(item.id);
      pushed += 1;
    } catch (error) {
      failed += 1;
      console.warn('[DrillLogPro] pending sync failed:', error?.message || error);
      // Preserve this and later items for a future retry.
    }
  }

  const remaining = (await store(SYNC_QUEUE_STORE).all())
    .filter((item) => item.user_id === userId).length;

  return { pushed, pending: remaining, failed };
}

export async function getPendingSync(userId) {
  const { store } = await import('@/storage/sqliteStore');
  const items = await store(SYNC_QUEUE_STORE).all();
  return items.filter((item) => item.user_id === userId);
}

export async function pullRecords(userId) {
  if (!isCloudEnabled() || !userId) return [];
  const organisationId = await getActiveTenantId();
  if (!organisationId) return [];
  const { data, error } = await supabase
    .from(CLOUD_TABLE)
    .select('entity,record_id,payload,updated_at,deleted')
    .eq('user_id', userId)
    .eq('organisation_id', organisationId);
  if (error) throw error;
  return data || [];
}


export async function pullOrganisationRecords(organisationId, entities = []) {
  if (!isCloudEnabled() || !organisationId) return [];
  const requested = Array.isArray(entities) ? entities.filter(Boolean) : [entities].filter(Boolean);
  let query = supabase
    .from(CLOUD_TABLE)
    .select('user_id,entity,record_id,payload,updated_at,deleted')
    .eq('organisation_id', organisationId);
  if (requested.length === 1) query = query.eq('entity', requested[0]);
  else if (requested.length > 1) query = query.in('entity', requested);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function syncFromCloud(userId, store) {
  if (!isCloudEnabled() || !userId) return { pulled: 0 };
  // Never pull an older cloud copy over a locally queued mutation.
  const pending = await getPendingSync(userId);
  const pendingKeys = new Set(pending.map((item) => `${item.entity}:${item.record_id}`));
  const records = await pullRecords(userId);
  let pulled = 0;
  for (const record of records) {
    const key = `${record.entity}:${record.record_id}`;
    if (pendingKeys.has(key)) continue;

    const table = store(record.entity);
    const local = await table.get(record.record_id);
    const cloudTime = new Date(record.updated_at || 0).getTime();
    const localTime = new Date(local?.updated_date || 0).getTime();
    if (record.deleted) {
      if (local && cloudTime >= localTime) await table.delete(record.record_id);
      continue;
    }
    if (!local || cloudTime >= localTime) {
      await table.put(record.payload);
      pulled += 1;
    }
  }
  return { pulled };
}

export async function syncNow(userId) {
  if (!userId || !isCloudEnabled()) return { pushed: 0, pending: 0, pulled: 0 };
  const pushedResult = await flushSyncQueue(userId);
  if (pushedResult.failed > 0) {
    return { ...pushedResult, pulled: 0 };
  }
  const { store } = await import('@/storage/sqliteStore');

  // Refresh the signed-in user's profile from the cloud before the general
  // pull so a cloud-assigned Owner/Admin role is restored locally.
  try {
    const cloudUser = await getCloudUser();
    if (cloudUser?.id === userId) {
      const profile = await getCloudProfile(userId);
      if (profile) await store('User').put(profile);
    }
  } catch (error) {
    console.warn('[DrillLogPro] cloud profile refresh skipped:', error?.message || error);
  }

  const pulledResult = await syncFromCloud(userId, store);
  return { ...pushedResult, ...pulledResult };
}
